import React from 'react';
import type { Showcase, ShowcaseProperty, ShowcaseResource } from '../../types';
import { Play } from 'lucide-react';

interface VideoSectionProps {
  showcase: Showcase;
  property: ShowcaseProperty;
  resources: ShowcaseResource[];
  brandColour: string;
  darkMode?: boolean;
}

function toEmbedUrl(url: string): string | null {
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;

  const vimeoMatch = url.match(/(?:vimeo\.com\/)(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;

  return null;
}

export default function VideoSection({ showcase, property, resources, brandColour, darkMode: dm }: VideoSectionProps) {
  const video = resources[0];
  if (!video) return null;

  const embedFromSource = video.source_url ? toEmbedUrl(video.source_url) : null;
  const embedUrl = video.embed_url || embedFromSource;
  const isEmbed = !!embedUrl;

  return (
    <section className={`py-16 ${dm ? 'bg-slate-800' : 'bg-slate-50'}`}>
      <div className="max-w-5xl mx-auto px-8">
        <h2 className={`text-2xl font-bold mb-8 ${dm ? 'text-white' : 'text-slate-900'}`}>
          {video.type === 'imagemotion_video' ? 'Property Video' : 'Video'}
        </h2>
        <div className="aspect-video rounded-xl overflow-hidden bg-black shadow-lg">
          {isEmbed ? (
            <iframe
              src={embedUrl!}
              className="w-full h-full"
              allow="autoplay; fullscreen"
              allowFullScreen
              title={video.title || 'Property video'}
            />
          ) : video.source_url ? (
            <video
              src={video.source_url}
              controls
              className="w-full h-full object-contain"
              poster={video.thumbnail_url || undefined}
            />
          ) : (
            <div className={`w-full h-full flex items-center justify-center ${dm ? 'bg-slate-950' : 'bg-slate-800'}`}>
              <Play className={`w-16 h-16 ${dm ? 'text-slate-400' : 'text-slate-500'}`} />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
